import {
	requestAnimationFrame,
	cancelAnimationFrame,
	is,
	qs,
	css,
	applyCoordinate,
	on,
	off,
	getCoordinates,
	getDroppable,
	getBoundX,
	getBoundY,
	getAncestorsScrolledTree,
	getPower,
	validateType,
	validateEvent,
	DraggerEvent
} from './utils';

export default class Dragger {
	draggable;
	droppable;
	container;
	options;
	emitters = {};
	modifiers;
	pluginDumps;

	static create(...props) {
		return new Dragger(...props);
	}

	constructor(container, options) {
		const d = document;
		const defaultOpt = {
			allowBoundContainer: true,
			allowExactDraggable: false,
			autoscroll: false,
			autoscrollSensitivity: 20,
			eventListenerOption: false
		};
		this.container = is.str(container)
			? qs(container)
			: is.node(container)
			? container
			: d.body;
		this.options = { ...defaultOpt, ...options };
		Object.keys(this.options).forEach((key) => {
			validateType(this.options[key], 
				[ key === 'autoscrollSensitivity' 
				? 'number' 
				: ['droppable', 'draggable', 'axis'].includes(key) 
					? 'string' 
					:  'boolean'
				], 
				`Dragger options \`${key}\``)
		})
		validateType(container, ['string', 'node'], 'Dragger argument `0`')
		validateType(this.container, ['node'], 'Dragger this.container is null check carefully what you input in argument `0`')
		this.init();
	}

	init() {
		const container = this.container;
		const { axis, draggable, droppable, allowBoundContainer, allowExactDraggable, autoscroll, autoscrollSensitivity, eventListenerOption } = this.options;
		/*DRAG-START*/
		this.initHandler = (startEvent) => {
			let droppableAt;
			let animationFrame;
			const {
				dragstart,
				dragmove,
				dragend,
				dragenter,
				dragover,
				dragexit,
				drop,
			} = this.emitters;
			const target = (!allowExactDraggable && startEvent.target.closest(draggable)) || startEvent.target;
			const isDraggable = target.matches(draggable);
			const commonSetting = { container, target, isDraggable, allowBoundContainer };
			const { x: startX, y: startY, ...startC } = getCoordinates(startEvent, commonSetting);
			const coordinates = {
				type: 'dragstart',
				target,
				container,
				isDraggableElement: isDraggable,
				startX: startX,
				startY: startY,
				moveX: startX,
				moveY: startY,
				endX: startX,
				endY: startY,
				clientX: startC.clientX,
				clientY: startC.clientY,
			};
			commonSetting.prefix = startC.prefix;
			is.fnc(dragstart) && dragstart.call(container, new DraggerEvent(startEvent, coordinates));
			isDraggable && applyCoordinate(target, startX, startY, axis);
			
			/*DRAG-MOVE*/
			const moveHandler = (moveEvent) => {
				const {clientX, clientY, ...moveC} = getCoordinates(moveEvent, commonSetting);
				coordinates.clientX = clientX;
				coordinates.clientY = clientY;
				let x = (coordinates.moveX = coordinates.endX = moveC.x);
				let y = (coordinates.moveY = coordinates.endY = moveC.y);
				if (isDraggable) {
					applyCoordinate(target, x, y, axis);
					/*AUTO-SCROLL WHEN EDGE*/
					if(autoscroll) {
						const sensitivity = autoscrollSensitivity 
						animationFrame && cancelAnimationFrame(animationFrame);
						const handle = () => {
							const bound = container.getBoundingClientRect()
							const prefix = commonSetting.prefix
							const requestAgain = getAncestorsScrolledTree(container).reduce((result, ancestorEL, i, arr) => {
								const { top, left } = ancestorEL.nodeName !== "HTML" ? ancestorEL.getBoundingClientRect() : {
									top: 0,
									left: 0 
								}
								let { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = ancestorEL 
								const position = {} 
								const isContainer = ancestorEL === container 
								const topBounds = top + sensitivity
								const bottomBounds = top + clientHeight - sensitivity
								const leftBounds = left + sensitivity
								const rightBounds = left + clientWidth - sensitivity
								const topSensitivityCut = topBounds - clientY
								const bottomSensitivityCut = clientY - bottomBounds
								const leftSensitivityCut = leftBounds - clientX
								const rightSensitivityCut = clientX - rightBounds
								const containerY = y + prefix.top
								const containerX = x + prefix.left
								const containerTop = bound.top
								const containerExceededTop = bound.top + bound.height
								const containerLeft = bound.left
								const containerExceededLeft = bound.left + bound.width
								const isEdgeTopScreen = 
									clientY < topBounds
									&& scrollTop > 0
									&& (isContainer ? containerY > 0 : clientY > containerTop)
								const isEdgeBottomScreen =
									clientY > bottomBounds  
									&& scrollTop < scrollHeight - clientHeight
									&& (isContainer ? containerY < scrollHeight : clientY < containerExceededTop);
								const isEdgeLeftScreen =
									clientX < leftBounds 
									&& scrollLeft > 0
									&& (isContainer ? containerX > 0 : clientX > containerLeft)
								const isEdgeRightScreen = 
									clientX > rightBounds
									&& scrollLeft < scrollWidth - clientWidth
									&& (isContainer ? containerX < scrollWidth : clientX < containerExceededLeft);
								const hasEdgeScreen = isEdgeTopScreen || isEdgeBottomScreen || isEdgeLeftScreen || isEdgeRightScreen
								
								if (isEdgeTopScreen) { 
									const power = getPower(sensitivity, topSensitivityCut, isContainer ? containerY - sensitivity : clientY - containerTop)
									scrollTop -= power;
									y -= power 
									position.top = `${getBoundY(y, prefix)}px`
								} else if (isEdgeBottomScreen) { 
									const power = getPower(sensitivity, bottomSensitivityCut, isContainer ? scrollHeight - containerY : containerExceededTop - clientY)
									scrollTop += power;
									y += power 
									position.top = `${getBoundY(y, prefix)}px` 
								}
								if (isEdgeLeftScreen) {
									const power = getPower(sensitivity, leftSensitivityCut, isContainer ? containerX - sensitivity : clientX - containerLeft)
									scrollLeft -= power;
									x -= power 
									position.left = `${getBoundX(x, prefix)}px`
								} else if (isEdgeRightScreen) { 
									const power = getPower(sensitivity, rightSensitivityCut, isContainer ? scrollWidth - containerX : containerExceededLeft - clientX)
									scrollLeft += power
									x += power
									position.left = `${getBoundX(x, prefix)}px` 
								} 
								if(hasEdgeScreen) {
									ancestorEL.scrollTop = scrollTop
									ancestorEL.scrollLeft = scrollLeft
									css(target, position)
									arr.slice(1)
								}
								return result || hasEdgeScreen
							}, false) 

							if(requestAgain)
								animationFrame = requestAnimationFrame(handle);
						}
					
						animationFrame = requestAnimationFrame(handle);
					}
					/*END - AUTO-SCROLL WHEN EDGE*/
					if (droppable) {
						const {
							currentDroppable,
							droppableNode,
							isOverDroppable,
						} = getDroppable({
							target,
							droppable,
							point: {
								x: coordinates.clientX,
								y: coordinates.clientY,
							},
						});
						if (isOverDroppable) {
							if (droppableAt !== currentDroppable) {
								is.fnc(dragenter) &&
									dragenter.call(currentDroppable, new DraggerEvent(moveEvent, {
										...coordinates,
										type: 'dragenter',
										currentDroppable,
										droppable: droppableNode,
									}));
								droppableAt &&
									is.fnc(dragexit) &&
									dragexit.call(currentDroppable, new DraggerEvent(moveEvent, {
										...coordinates,
										type: 'dragexit',
										currentDroppable: droppableAt,
									}));
							}
							droppableAt = currentDroppable;
							is.fnc(dragover) 
								&& dragover.call(container, new DraggerEvent(moveEvent, {
									...coordinates, 
									type: 'dragover',
									currentDroppable,
									droppable: droppableNode,
								}));
						}
					}
				}
				is.fnc(dragmove) && dragmove.call(container, new DraggerEvent(moveEvent, {...coordinates, type: 'dragmove'}));
			};

			on(container, 'mousemove touchmove', moveHandler, eventListenerOption);

			/*DRAG-END*/
			const endHandler = (endEvent) => { 
				if(autoscroll && animationFrame) {
					cancelAnimationFrame(animationFrame)
				}
					
				if (droppable && isDraggable) {
					if (isDraggable) {
						css(target, {
							position: null,
							left: null,
							top: null,
						});
					}
					const {
						currentDroppable,
						droppableNode,
						isOverDroppable,
					} = getDroppable({
						target,
						droppable,
						point: {
							x: coordinates.clientX,
							y: coordinates.clientY,
						},
					});
					isOverDroppable &&
						is.fnc(drop) &&
						drop.call(currentDroppable, new DraggerEvent(endEvent, {
							...coordinates,
							type: 'drop',
							currentDroppable,
							droppable: droppableNode,
						}));
				}
				
				is.fnc(dragend) &&
					dragend.call(container, new DraggerEvent(endEvent, {...coordinates, type: 'dragend'}));
					
				off(container, 'mousemove touchmove', moveHandler, eventListenerOption);
				off(container, 'mouseup touchend', endHandler, eventListenerOption);
			};

			on(container, 'mouseup touchend', endHandler, eventListenerOption);
		};
		on(container, 'mousedown touchstart', this.initHandler, eventListenerOption);
	}

	on(eventType, callback) {
		validateEvent('on', eventType)
		this.emitters[eventType] = callback;
	}

	off(eventType) {
		validateEvent('off', eventType)
		delete this.emitters[eventType];
	}

	destroy() {
		off(this.container, 'mousedown touchstart', this.initHandler, this.options.eventListenerOption);
	}
}
